import React from 'react';
import {
  Users, Star, IdCard, UserPlus, GraduationCap, Handshake, MessageSquare, Briefcase, Heart,
  Megaphone, Target, Mail, ThumbsUp, Globe, Camera, Filter,
  DollarSign, BarChart3, Calculator, PieChart, TrendingUp, PiggyBank, Percent, Wallet, Receipt,
  Workflow, Settings, ClipboardCheck, CalendarCheck, Timer, HardHat, Wrench, Boxes, ListChecks,
  Bot, Cpu, Brain, Binary, Code, Network, SquareTerminal, RefreshCw, CircuitBoard,
  Phone, Trophy,
  TreePalm, Sun, Waves, Sailboat, Shell, Umbrella, Fish, Anchor,
} from 'lucide-react';

// Animated department-themed background for the Next Breakthrough Session card.
// Ported from the DC "Background <Dept>" designs: gradient + drifting BBA emblem
// watermarks + dash-flow circuit traces + floating department icons.

// Base scatter placements: [top%, left%, sizePx, baseDur, delay, dx, dy, r0, r1, pulse]
const BASE_PLACEMENTS = [
  [6, 74, 38, 6, 0.0, -8, -12, -6, 6, 1],
  [21, 87, 30, 7, 1.2, -10, 10, 8, -6, 0],
  [13, 54, 28, 8, 0.5, 6, -10, -4, 6, 1],
  [34, 5, 28, 7, 2.0, 8, -14, -8, 6, 1],
  [47, 86, 28, 8, 0.8, -10, -8, 6, -8, 0],
  [57, 7, 32, 8, 1.6, 10, -10, -6, 8, 1],
  [55, 45, 26, 6, 2.4, -6, -14, 6, -6, 1],
  [70, 83, 32, 9, 0.3, -8, 12, -6, 8, 0],
  [80, 7, 26, 7, 1.0, 10, -12, -8, 6, 1],
  [88, 58, 30, 8, 1.8, 8, -10, 6, -8, 0],
  [72, 29, 24, 6, 0.6, -6, -12, -6, 8, 1],
  [5, 30, 22, 7, 1.4, 8, 10, -6, 6, 0],
];

const CIRCUITS = {
  diag: {
    paths: [
      'M-10 130 L60 96 L130 118 L210 60 L290 84 L393 30',
      'M-10 400 L80 360 L160 386 L250 320 L330 348 L393 300',
    ],
    nodes: [[60, 96], [130, 118], [210, 60], [290, 84], [80, 360], [250, 320], [330, 348]],
  },
  ortho: {
    paths: [
      'M-10 60 H40 V118 H120 M120 118 H190 V74 H258 M258 74 H330 V210 M330 210 H393',
      'M-10 330 H96 V260 H200 M200 260 V386 H300 M300 386 H393',
    ],
    nodes: [[40, 60], [120, 118], [258, 74], [330, 210], [96, 330], [300, 386], [200, 260]],
  },
  wave: {
    paths: [
      'M-10 100 C 40 80, 90 120, 140 100 S 240 80, 290 100 S 370 110, 393 95',
      'M-10 370 C 50 350, 110 390, 170 370 S 280 350, 330 370 S 380 380, 393 372',
    ],
    nodes: [[60, 92], [160, 102], [260, 92], [350, 100], [80, 358], [220, 366], [330, 368]],
  },
};

const THEMES = {
  HR: {
    gradient: 'linear-gradient(135deg, #6e5303 0%, #a37f06 35%, #d0a90c 68%, #f2d23c 100%)',
    accent: '#6e5303',
    circuit: 'diag',
    icons: [Users, Star, IdCard, UserPlus, GraduationCap, Handshake, MessageSquare, Briefcase, Heart, Users, Star, GraduationCap],
  },
  Marketing: {
    gradient: 'linear-gradient(135deg, #7a2e04 0%, #c2570a 35%, #ee7c12 68%, #ffb43c 100%)',
    accent: '#7a2e04',
    circuit: 'diag',
    icons: [Megaphone, Heart, Target, Mail, ThumbsUp, Globe, MessageSquare, Camera, Filter, Megaphone, Heart, MessageSquare],
  },
  Finance: {
    gradient: 'linear-gradient(135deg, #06422a 0%, #0b7a44 35%, #12a35c 68%, #3dd68c 100%)',
    accent: '#06422a',
    circuit: 'diag',
    icons: [DollarSign, BarChart3, Calculator, PieChart, TrendingUp, PiggyBank, Percent, Wallet, Receipt, DollarSign, BarChart3, Percent],
  },
  Operations: {
    gradient: 'linear-gradient(135deg, #082a63 0%, #0d47a8 35%, #1668d9 68%, #2fa8f0 100%)',
    accent: '#082a63',
    circuit: 'ortho',
    icons: [Workflow, Settings, ClipboardCheck, CalendarCheck, Timer, HardHat, Wrench, Boxes, ListChecks, Workflow, Settings, ClipboardCheck],
    // Gears swing rotation instead of drifting tilt
    rotationOverrides: { 1: [0, 35], 10: [0, -35] },
  },
  Sales: {
    gradient: 'linear-gradient(135deg, #6b0606 0%, #a30707 35%, #e11212 68%, #ff4d33 100%)',
    accent: '#6b0606',
    circuit: 'diag',
    icons: [TrendingUp, DollarSign, Target, Megaphone, Trophy, Handshake, Phone, Filter, Percent, TrendingUp, DollarSign, Target],
  },
  Technology: {
    gradient: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 32%, #a72ce0 62%, #d926c9 100%)',
    accent: '#5b21b6',
    circuit: 'ortho',
    icons: [Bot, Cpu, Brain, Binary, Code, Network, SquareTerminal, RefreshCw, Cpu, Bot, CircuitBoard, Binary],
    rotationOverrides: { 7: [0, 30] },
  },
  Summit: {
    gradient: 'linear-gradient(135deg, #04355c 0%, #085f8a 35%, #0e93b8 68%, #3ed6d0 100%)',
    accent: '#04355c',
    circuit: 'wave',
    icons: [TreePalm, Sun, Waves, Sailboat, Shell, Star, Umbrella, Fish, Anchor, Waves, Fish, Star],
    // Sun swings rotation instead of drifting tilt
    rotationOverrides: { 1: [0, 25] },
  },
  General: {
    gradient: 'linear-gradient(135deg, #312e81 0%, #6d28d9 35%, #9333ea 68%, #c026d3 100%)',
    accent: '#4338ca',
    circuit: 'diag',
    icons: null,
  },
};

export function getBreakthroughTheme(department) {
  return THEMES[department] || THEMES.General;
}

const ICON_OPACITY = 0.4;

export default function BreakthroughSessionBackground({ department }) {
  const theme = getBreakthroughTheme(department);
  const circuit = CIRCUITS[theme.circuit];

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <style>{`
        @keyframes bsbIcoFloat {
          0%   { transform: translate(0, 0) rotate(var(--bsb-r0, 0deg)); }
          50%  { transform: translate(var(--bsb-dx, 8px), var(--bsb-dy, -14px)) rotate(var(--bsb-r1, 8deg)); }
          100% { transform: translate(0, 0) rotate(var(--bsb-r0, 0deg)); }
        }
        @keyframes bsbBlobDrift {
          0%   { transform: translate(0, 0) scale(1); }
          50%  { transform: translate(var(--bsb-bx, 30px), var(--bsb-by, -24px)) scale(1.08); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes bsbIcoPulse {
          0%, 100% { opacity: var(--bsb-o0, 0.35); }
          50%      { opacity: var(--bsb-o1, 0.7); }
        }
        @keyframes bsbDashFlow { to { stroke-dashoffset: -120; } }
        @keyframes bsbNodeBlink { 0%,100% { opacity: .2; } 50% { opacity: .9; } }
      `}</style>

      {/* translucent emblem watermarks drifting in the background */}
      <img
        src="/bba-emblem.png"
        alt=""
        className="absolute"
        style={{
          top: -70, right: -60, width: 240, height: 240,
          filter: 'brightness(0) invert(1)', opacity: 0.09,
          '--bsb-bx': '-22px', '--bsb-by': '16px',
          animation: 'bsbBlobDrift 16s ease-in-out infinite',
        }}
      />
      <img
        src="/bba-emblem.png"
        alt=""
        className="absolute"
        style={{
          bottom: -90, right: -30, width: 280, height: 280,
          filter: 'brightness(0) invert(1)', opacity: 0.07,
          '--bsb-bx': '18px', '--bsb-by': '-22px',
          animation: 'bsbBlobDrift 20s ease-in-out infinite',
        }}
      />
      <img
        src="/bba-emblem.png"
        alt=""
        className="absolute"
        style={{
          bottom: 60, left: -100, width: 200, height: 200,
          filter: 'brightness(0) invert(1)', opacity: 0.08,
          '--bsb-bx': '26px', '--bsb-by': '-12px',
          animation: 'bsbBlobDrift 18s ease-in-out infinite',
        }}
      />

      {/* circuit traces */}
      <svg
        viewBox="0 0 383 452"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        style={{ opacity: 0.35 }}
      >
        <path
          d={circuit.paths[0]}
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="1"
          fill="none"
          strokeDasharray="6 10"
          style={{ animation: 'bsbDashFlow 10s linear infinite' }}
        />
        <path
          d={circuit.paths[1]}
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="1"
          fill="none"
          strokeDasharray="4 12"
          style={{ animation: 'bsbDashFlow 14s linear infinite reverse' }}
        />
        {circuit.nodes.map((n, i) => (
          <circle
            key={i}
            cx={n[0]}
            cy={n[1]}
            r="2.4"
            fill="#ffffff"
            style={{ animation: `bsbNodeBlink ${2.5 + i * 0.6}s ease-in-out ${-i * 0.7}s infinite` }}
          />
        ))}
      </svg>

      {/* floating department icons */}
      {theme.icons && (
        <div className="absolute inset-0">
          {BASE_PLACEMENTS.map((p, i) => {
            const Icon = theme.icons[i];
            if (!Icon) return null;
            const [top, left, size, dur, delay, dx, dy, r0, r1, pulse] = p;
            const [rot0, rot1] = theme.rotationOverrides?.[i] || [r0, r1];
            return (
              <div
                key={i}
                className="absolute"
                style={{
                  top: `${top}%`, left: `${left}%`,
                  width: size, height: size, opacity: ICON_OPACITY,
                  '--bsb-dx': `${dx}px`, '--bsb-dy': `${dy}px`,
                  '--bsb-r0': `${rot0}deg`, '--bsb-r1': `${rot1}deg`,
                  '--bsb-o0': ICON_OPACITY - 0.15, '--bsb-o1': ICON_OPACITY + 0.3,
                  animation:
                    `bsbIcoFloat ${dur}s ease-in-out ${-delay}s infinite` +
                    (pulse ? `, bsbIcoPulse ${dur * 0.7}s ease-in-out ${-delay}s infinite` : ''),
                }}
              >
                <Icon className="w-full h-full text-white" strokeWidth={1.6} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
